/**
 * TextField for SchemaForm — renders a JSON-Schema `string` property.
 * If `format: 'textarea'` is set, renders a textarea; otherwise a single-line input.
 *
 * This is intentionally distinct from the top-level `components/TextField/`:
 * SchemaForm fields are driven by JSON Schema metadata and live inside a
 * FormProvider, not as standalone primitives.
 */

import { useFormContext, type FieldValues } from 'react-hook-form';
import styles from '../SchemaForm.module.css';
import type { JsonSchemaProperty } from '../types';

export interface SchemaFieldProps {
  name: string;
  sub: JsonSchemaProperty;
  required: boolean;
}

export function TextField({ name, sub, required }: SchemaFieldProps) {
  const {
    register,
    formState: { errors },
  } = useFormContext<FieldValues>();
  const isTextarea = sub.format === 'textarea';
  const error = errors[name];
  return (
    <label className={styles.field}>
      <span>
        {name}
        {required ? ' *' : ''}
      </span>
      {isTextarea ? (
        <textarea {...register(name, { required })} aria-label={name} />
      ) : (
        <input type="text" {...register(name, { required })} aria-label={name} />
      )}
      {sub.description && <small>{sub.description}</small>}
      {error && (
        <small className={styles.error}>
          {typeof error.message === 'string' && error.message.length > 0
            ? error.message
            : 'Required'}
        </small>
      )}
    </label>
  );
}
